"use client";

import React, { useState, useMemo } from "react";
import { METRICS_EVENTS } from "@cede/types";
import { type CurrencyMetadata } from "@cede/types";
import { Button } from "@/components/ui/button";
import { PortalModal } from "@/components/ui/portal-modal";
import { POPULAR_CURRENCY_CODES } from "@/constants";
import { useUserCurrency, useSupportedCurrencies } from "@/hooks/currency";
import { useMetricsEvents } from "@/hooks/data/useMetricsEvents";
import { mixpanelClient } from "@/lib/mixpanel";
import { cn } from "@/lib/utils";
import { Globe, SearchIcon, Check } from "lucide-react";

interface CurrencyCardProps {
  currency: CurrencyMetadata;
  isSelected: boolean;
  onSelect: (currency: CurrencyMetadata) => void;
}

const CurrencyCard: React.FC<CurrencyCardProps> = ({ currency, isSelected, onSelect }) => (
  <button
    onClick={() => onSelect(currency)}
    className={cn(
      "relative flex flex-col items-start gap-1 p-4 rounded-lg text-left",
      "border transition-all duration-200",
      "hover:bg-cede-primary/10 hover:border-cede-primary/40",
      "focus:outline-none focus:ring-2 focus:ring-cede-primary/50",
      isSelected ? "bg-cede-primary/15 border-cede-primary/50" : "bg-cede-bg-component border-cede-border-container",
    )}
  >
    {isSelected && (
      <div className="absolute top-2 right-2">
        <Check className="h-4 w-4 text-cede-primary" />
      </div>
    )}
    <div className="flex items-center gap-2">
      <span className="text-xl">{currency.flagEmoji}</span>
      <span className={cn("font-semibold", isSelected ? "text-cede-primary" : "text-cede-text-secondary")}>
        {currency.codeUpper}
      </span>
    </div>
    <span className="text-sm text-cede-text-primary font-medium leading-tight">{currency.name}</span>
  </button>
);

interface CurrencySelectorProps {
  className?: string;
  source?: string;
}

/**
 * Currency selector with globe icon trigger and modal
 * Allows users to select their preferred display currency
 */
export const CurrencySelector: React.FC<CurrencySelectorProps> = ({ className, source = "unknown" }) => {
  const { trackEvent } = useMetricsEvents();
  const { selectedCurrency, setSelectedCurrency, currencyMetadata } = useUserCurrency();
  const supportedCurrencies = useSupportedCurrencies();
  const [searchQuery, setSearchQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  // Get popular currencies in the specified order (filtered by search)
  const popularCurrencies = useMemo(() => {
    const popular = POPULAR_CURRENCY_CODES.map((code) => supportedCurrencies.find((c) => c.code === code)).filter(
      Boolean,
    ) as CurrencyMetadata[];

    if (!searchQuery.trim()) return popular;

    const query = searchQuery.toLowerCase();
    return popular.filter(
      (currency) =>
        currency.code.toLowerCase().includes(query) ||
        currency.name.toLowerCase().includes(query) ||
        currency.codeUpper.includes(query.toUpperCase()),
    );
  }, [supportedCurrencies, searchQuery]);

  // Get other currencies (non-popular), sorted alphabetically with selected first
  const otherCurrencies = useMemo(() => {
    const popularSet = new Set<string>(POPULAR_CURRENCY_CODES);
    let others = supportedCurrencies.filter((c) => !popularSet.has(c.code));

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      others = others.filter(
        (currency) =>
          currency.code.toLowerCase().includes(query) ||
          currency.name.toLowerCase().includes(query) ||
          currency.codeUpper.includes(query.toUpperCase()),
      );
    }

    return [...others].sort((a, b) => {
      if (a.code === selectedCurrency) return -1;
      if (b.code === selectedCurrency) return 1;
      return a.name.localeCompare(b.name);
    });
  }, [supportedCurrencies, searchQuery, selectedCurrency]);

  const hasResults = popularCurrencies.length > 0 || otherCurrencies.length > 0;

  const handleSelect = (currency: CurrencyMetadata) => {
    const previousCurrency = selectedCurrency;
    setSelectedCurrency(currency.code);
    // `setPeople` is intentionally client-side per analytics guidelines.
    mixpanelClient.setPeople({
      preferredCurrency: currency.code,
    });
    trackEvent(METRICS_EVENTS.CURRENCY_SELECTED, {
      currency: currency.code,
      previousCurrency,
      source,
    });
    setSearchQuery("");
    setIsOpen(false);
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setSearchQuery("");
    }
  };

  return (
    <>
      {/* Globe icon trigger button */}
      <Button
        variant="secondary"
        size="small"
        onClick={() => setIsOpen(true)}
        className={cn("relative min-w-0 !px-2", className)}
        aria-label="Select currency"
      >
        <Globe size={20} strokeWidth={2.2} className="text-cede-primary !h-[20px] !w-[20px]" />
        {/* Currency symbol indicator */}
        {currencyMetadata && (
          <span
            className={cn(
              "absolute -right-1 -top-0.5",
              "flex items-center justify-center",
              "min-w-[14px] h-[14px] px-0.5",
              "text-[9px] font-bold leading-none",
              "rounded-full",
              "bg-cede-primary text-cede-bg-primary",
              "ring-2 ring-cede-bg-primary",
            )}
            aria-hidden="true"
          >
            {currencyMetadata.symbol}
          </span>
        )}
      </Button>

      {/* Currency selection modal */}
      <PortalModal open={isOpen} onOpenChange={handleOpenChange} className="w-full max-w-3xl max-h-[85vh]">
        <PortalModal.Header>
          <PortalModal.Title>Select Currency</PortalModal.Title>
          <PortalModal.Description>
            Choose your preferred display currency for rewards and balances
          </PortalModal.Description>
        </PortalModal.Header>

        <PortalModal.Content scrollable className="gap-4">
          {/* Search input */}
          <div className="flex items-center gap-2 rounded-lg border border-cede-border-container bg-cede-bg-component px-3 py-2">
            <SearchIcon className="h-4 w-4 text-cede-text-tertiary shrink-0" />
            <input
              placeholder="Search currencies..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-transparent text-sm text-cede-text-primary placeholder:text-cede-text-tertiary outline-none"
            />
          </div>

          {/* Currency grid */}
          {!hasResults ? (
            <div className="py-8 text-center text-cede-text-secondary">No currencies found</div>
          ) : (
            <div className="flex flex-col gap-6">
              {/* Popular currencies section */}
              {popularCurrencies.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-cede-text-secondary mb-3">Popular</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {popularCurrencies.map((currency) => (
                      <CurrencyCard
                        key={currency.code}
                        currency={currency}
                        isSelected={selectedCurrency === currency.code}
                        onSelect={handleSelect}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Other currencies section */}
              {otherCurrencies.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-cede-text-secondary mb-3">Other</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {otherCurrencies.map((currency) => (
                      <CurrencyCard
                        key={currency.code}
                        currency={currency}
                        isSelected={selectedCurrency === currency.code}
                        onSelect={handleSelect}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </PortalModal.Content>
      </PortalModal>
    </>
  );
};
